<template>
  <Transition
    enter-active-class="duration-300"
    enter-from-class="opacity-0"
    enter-to-class="opacity-100"
    leave-active-class="duration-300"
    leave-from-class="opacity-100"
    leave-to-class="opacity-0"
  >
    <div
      v-if="!isWidescreen && isMobileSidebarOpen"
      class="absolute inset-0 z-20 bg-black/50 ease-bounce motion-safe:transition"
      @click="hideSidebar"
    ></div>
  </Transition>

  <Transition
    enter-active-class="duration-300 ease-bounce"
    enter-from-class="-translate-x-full"
    enter-to-class="translate-x-0"
    leave-active-class="duration-300 ease-bounce"
    leave-from-class="translate-x-0"
    leave-to-class="-translate-x-full"
  >
    <div
      v-if="isWidescreen ? isDesktopSidebarOpen : isMobileSidebarOpen"
      class="absolute inset-y-0 left-0 z-20 flex flex-col border-r border-gray-300 bg-gray-50 transition-all motion-reduce:transition-none dark:border-border dark:bg-sidebar"
      :class="[isWidescreen ? `w-52` : `w-60`]"
    >
      <div
        class="flex h-[53px] min-w-0 shrink-0 items-center justify-between gap-2 px-2 pl-2.5"
      >
        <SwitchNewsletter />

        <div class="flex min-w-0 shrink-0 items-center gap-2">
          <button
            type="button"
            aria-label="Close sidebar"
            class="rounded-full p-[0.4375rem] text-gray-700 outline-2 outline-blue-500 transition hover:bg-gray-200 focus-visible:outline active:bg-gray-200 dark:text-muted-foreground dark:hover:bg-card dark:active:bg-card"
            @click="hideSidebar"
          >
            <component
              :is="isWidescreen ? MenuIcon : CloseIcon"
              class="size-4"
            />
          </button>
        </div>
      </div>

      <div class="flex grow flex-col overflow-y-auto px-2 pb-2">
        <SidebarItem
          :item="{
            icon: SearchIcon,
            title: 'Search',
            action: () => {
              emit('search');
            },
          }"
        />

        <template v-for="item in mainNavigation" :key="item.icon">
          <SidebarItem :item="item" />
          <div
            v-if="item.title === 'Home' && bookmarks.length > 0"
            class="ml-4 flex flex-col pl-2"
          >
            <template v-for="bookmark in bookmarks" :key="bookmark.path">
              <SidebarItem
                :item="{
                  icon: getBookmarkIcon(bookmark),
                  title: bookmark.label || getBookmarkTitle(bookmark),
                  route: getBookmarkRoute(bookmark),
                }"
                :active="isBookmarkActive(bookmark)"
              />
            </template>
          </div>
        </template>

        <SidebarCollapsible
          v-if="apiNavigation.length > 0"
          v-model="isApiExpanded"
          title="API"
        >
          <template v-for="item in apiNavigation" :key="item.icon">
            <SidebarItem :item="item" />
          </template>
        </SidebarCollapsible>

        <SidebarCollapsible
          v-if="settingsNavigation.length > 0"
          v-model="isSettingsExpanded"
          title="Settings"
        >
          <template v-for="item in settingsNavigation" :key="item.icon">
            <SidebarItem :item="item" />
          </template>
        </SidebarCollapsible>

        <SidebarCollapsible
          v-if="accountNavigation.length > 0"
          v-model="isAccountExpanded"
          title="Account"
        >
          <template v-for="item in accountNavigation" :key="item.icon">
            <SidebarItem :item="item" />
          </template>
        </SidebarCollapsible>

        <div class="mt-auto flex shrink-0 flex-col pt-4">
          <HelpItem />

          <SidebarItem
            :item="{
              icon: LogoutIcon,
              title: 'Log out',
              action: () => {
                isLogoutOpen = true;
              },
            }"
          />
        </div>
      </div>
    </div>
  </Transition>

  <Dialog v-if="isLogoutOpen" title="Log out?" @close="isLogoutOpen = false">
    <template #actions>
      <ActionButton
        text="Log out"
        icon="arrow-right-start-on-rectangle"
        variant="primary"
        @click="accountStore.logout()"
      />
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router/auto";

import ActionButton from "@/components/Utilities/ActionButton.vue";
import Dialog from "@/design_system/Dialog/Dialog.vue";
import LogoutIcon from "@/icons/heroicons/arrow-right-start-on-rectangle-micro.svg";
import MenuIcon from "@/icons/heroicons/bars-3-micro.svg";
import SearchIcon from "@/icons/heroicons/magnifying-glass-micro.svg";
import StarIcon from "@/icons/heroicons/star-micro.svg";
import CloseIcon from "@/icons/heroicons/x-mark-micro.svg";
import { IS_DEMO } from "@/lib/environment";
import { useMediaQuery } from "@/lib/media_query";
import type { RouteLocation } from "@/router/types";
import { SEARCH_CONTEXT } from "@/search/routes";
import {
  useAccount,
  useAccountStore,
  useNewsletter,
  useNewsletterPermissions,
} from "@/store/stores/account";
import { useStore as useUIStatesStore } from "@/store/stores/ui-states";

import HelpItem from "./internal/HelpItem.vue";
import SidebarCollapsible from "./internal/SidebarCollapsible.vue";
import SidebarItem from "./internal/SidebarItem.vue";
import SwitchNewsletter from "./internal/SwitchNewsletter.vue";
import {
  ACCOUNT_NAVIGATION,
  API_NAVIGATION,
  canShowRoute,
  MAIN_NAVIGATION,
  SETTINGS_NAVIGATION,
} from "./lib";

const emit = defineEmits<{
  search: [];
}>();

const router = useRouter();
const route = useRoute();

const accountStore = useAccountStore();
const uiStates = useUIStatesStore();

const account = useAccount();
const newsletter = useNewsletter();
const permissions = useNewsletterPermissions();

const hideSidebarParam = computed(() => {
  const hideSidebar = route.query.hideSidebar;
  return hideSidebar === "true" || hideSidebar === "1";
});

const isDesktopSidebarOpen = computed(() => {
  return !hideSidebarParam.value && uiStates.isDesktopSidebarOpen;
});

const isMobileSidebarOpen = computed(() => {
  return !hideSidebarParam.value && uiStates.isMobileSidebarOpen;
});

const isWidescreen = useMediaQuery("(width >= 1024px)");

const isOnApi = computed(() => {
  const path = router.currentRoute.value.path;
  return (
    path.startsWith("/events") ||
    path.startsWith("/keys") ||
    path.startsWith("/platform") ||
    path.startsWith("/requests") ||
    path.startsWith("/webhooks")
  );
});

const SETTINGS_PATHS = [
  "/settings/general",
  "/settings/archives",
  "/settings/email",
  "/settings/embedding",
  "/settings/subscribing",
  "/settings/integrations",
  "/settings/paid-subscriptions",
  "/settings/team",
  "/settings/data",
];

const ACCOUNT_PATHS = [
  "/settings/profile",
  "/settings/notifications",
  "/settings/security",
  "/settings/referrals",
  "/settings/billing",
  "/settings/danger-zone",
];

const isOnSettings = computed(() => {
  const path = router.currentRoute.value.path;
  return SETTINGS_PATHS.some((p) => path.startsWith(p));
});

const isOnAccount = computed(() => {
  const path = router.currentRoute.value.path;
  return ACCOUNT_PATHS.some((p) => path.startsWith(p));
});

const isLogoutOpen = ref(false);
const isApiExpanded = ref(false);
const isSettingsExpanded = ref(false);
const isAccountExpanded = ref(false);

// Auto-expand sections when navigating to them
watch(
  isOnApi,
  (value) => {
    if (value) isApiExpanded.value = true;
  },
  { immediate: true }
);

watch(
  isOnSettings,
  (value) => {
    if (value) isSettingsExpanded.value = true;
  },
  { immediate: true }
);

watch(
  isOnAccount,
  (value) => {
    if (value) isAccountExpanded.value = true;
  },
  { immediate: true }
);

const mainNavigation = computed(() => {
  return MAIN_NAVIGATION.filter((route) => {
    return canShowRoute(route, {
      newsletter: newsletter.value,
      permissions: permissions.value,
      isDemo: IS_DEMO,
    });
  });
});

const settingsNavigation = computed(() => {
  return SETTINGS_NAVIGATION.filter((route) => {
    return canShowRoute(route, {
      newsletter: newsletter.value,
      permissions: permissions.value,
      isDemo: IS_DEMO,
    });
  });
});

const accountNavigation = computed(() => {
  return ACCOUNT_NAVIGATION.filter((route) => {
    return canShowRoute(route, {
      newsletter: newsletter.value,
      permissions: permissions.value,
      isDemo: IS_DEMO,
    });
  });
});

const apiNavigation = computed(() => {
  const hasMultipleNewsletters =
    account.value &&
    account.value.permissions &&
    account.value.permissions.length > 4;

  return API_NAVIGATION.filter((config) => {
    // Platform only shows for accounts with 4+ newsletters
    if (
      "route" in config &&
      config.route &&
      "name" in config.route &&
      config.route.name === "/platform"
    ) {
      if (!hasMultipleNewsletters) return false;
    }

    return canShowRoute(config, {
      newsletter: newsletter.value,
      permissions: permissions.value,
      isDemo: IS_DEMO,
    });
  });
});

const bookmarks = computed(() => {
  const sidebarRouteNames = new Set<string>();
  for (const item of mainNavigation.value) {
    if (item.route && "name" in item.route) {
      const routeName = item.route.name;
      if (typeof routeName === "string") {
        sidebarRouteNames.add(routeName);
      }
    }
  }

  return uiStates.bookmarks.filter((bookmark) => {
    if (!(bookmark.name in SEARCH_CONTEXT)) {
      return false;
    }

    const bookmarkPath = bookmark.path || bookmark.name;
    const [basePath, queryString] = bookmarkPath.split("?");

    // If the base path matches a sidebar route, only show it if it has query params
    if (sidebarRouteNames.has(basePath)) {
      return !!queryString;
    }

    return true;
  });
});

const getBookmarkTitle = (bookmark: (typeof uiStates.bookmarks)[0]) => {
  const meta = SEARCH_CONTEXT[bookmark.name as keyof typeof SEARCH_CONTEXT];
  return meta?.title || bookmark.name;
};

const getBookmarkIcon = (bookmark: (typeof uiStates.bookmarks)[0]) => {
  const meta = SEARCH_CONTEXT[bookmark.name as keyof typeof SEARCH_CONTEXT];
  return meta?.icon || StarIcon;
};

const getBookmarkRoute = (
  bookmark: (typeof uiStates.bookmarks)[0]
): RouteLocation => {
  const path = bookmark.path || bookmark.name;
  const [, queryString] = path.split("?");
  const query: Record<string, string | string[]> = {};

  if (queryString) {
    const params = new URLSearchParams(queryString);
    for (const [key, value] of params.entries()) {
      if (query[key]) {
        if (Array.isArray(query[key])) {
          (query[key] as string[]).push(value);
        } else {
          query[key] = [query[key] as string, value];
        }
      } else {
        query[key] = value;
      }
    }
  }

  return queryString
    ? ({
        name: bookmark.name,
        query: query,
      } as RouteLocation)
    : ({
        name: bookmark.name,
      } as RouteLocation);
};

const isBookmarkActive = (bookmark: (typeof uiStates.bookmarks)[0]) => {
  const currentRoute = router.currentRoute.value;
  const currentPath = currentRoute.path;
  const currentQuery = currentRoute.query;
  const bookmarkPath = bookmark.path || bookmark.name;
  const [pathOnly, queryString] = bookmarkPath.split("?");

  // Check if path matches exactly
  if (currentPath !== pathOnly) {
    return false;
  }

  // If bookmark has no query params, check that current route also has none
  if (!queryString) {
    return Object.keys(currentQuery).length === 0;
  }

  // Parse bookmark query params
  const bookmarkParams = new URLSearchParams(queryString);
  const bookmarkQueryKeys = new Set<string>();

  // Build expected query params from bookmark
  for (const [key, value] of bookmarkParams.entries()) {
    const baseKey = key.endsWith("[]") ? key.slice(0, -2) : key;
    bookmarkQueryKeys.add(baseKey);

    const currentValue = currentQuery[baseKey];

    // Check if the value matches
    if (Array.isArray(currentValue)) {
      if (!currentValue.includes(value)) {
        return false;
      }
    } else if (currentValue !== value) {
      return false;
    }
  }

  // Check that all bookmark params exist in current query
  // (but allow current query to have extra params)
  for (const key of bookmarkQueryKeys) {
    if (!(key in currentQuery)) {
      return false;
    }
  }

  return true;
};

const hideSidebar = () => {
  if (isWidescreen.value) {
    uiStates.isDesktopSidebarOpen = false;
  } else {
    uiStates.isMobileSidebarOpen = false;
  }
};
</script>
